import { getTextStyles, squareBlockCSS, styledOmitCustomProps, Tooltip } from '@braskai/ui-kit';

import { alpha, Box, ButtonBase, css, styled, tooltipClasses } from '@mui/material';

export const SIDEBAR_ITEM_ACTION_CLASS = 'desktop-sidebar-item-action';

type SItemProps = {
  $isOpen?: boolean;
  $selected?: boolean;
  $variant: 'primary' | 'secondary';
};

export const SGroup = styled('div')(
  () => css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0 8px;
  `,
);

export const SMainGroup = styled(SGroup)(
  () => css`
    padding-top: 4px;
  `,
);

export const SHelpGroup = styled(SGroup)(
  () => css`
    padding-top: 4px;
    padding-bottom: 4px;
  `,
);

export const SSectionTitle = styled(
  Box,
  styledOmitCustomProps,
)<{ $isOpen?: boolean }>(
  ({ theme, $isOpen }) => css`
    flex-shrink: 0;
    padding: 20px 20px 6px;
    font-family: ${theme.typography.fontFamily};
    font-size: 16px;
    line-height: 20px;
    white-space: nowrap;
    opacity: ${$isOpen ? 1 : 0};
    transition: opacity ${theme.transitions.duration.shorter}ms;

    & > span {
      font-size: 11px;
      font-weight: 600;
      line-height: 1;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      color: ${theme.palette.grey[400]};
    }
  `,
);

export const SItem = styled(
  Box,
  styledOmitCustomProps,
)<SItemProps>(
  ({ theme, $selected, $variant }) => css`
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    height: ${$variant === 'primary' ? 44 : 40}px;
    border-radius: 10px;
    overflow: hidden;
    background-color: ${$selected ? theme.palette.grey[100] : 'transparent'};
    transition: background-color ${theme.transitions.duration.shortest}ms;

    &:hover {
      background-color: ${$selected ? theme.palette.grey[100] : theme.palette.grey[50]};
    }

    &:hover .${SIDEBAR_ITEM_ACTION_CLASS}, &:has(:focus-visible) .${SIDEBAR_ITEM_ACTION_CLASS} {
      opacity: 1;
    }
  `,
);

export const SItemButton = styled(
  ButtonBase,
  styledOmitCustomProps,
)<SItemProps>(
  ({ theme, $variant }) => css`
    font-family: ${theme.typography.fontFamily};
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    min-width: 0;
    height: 100%;
    padding: 0 12px;
    text-align: left;
    white-space: nowrap;
    color: ${$variant === 'primary' ? theme.palette.grey[900] : theme.palette.grey[600]};
    ${getTextStyles($variant === 'primary' ? 'body-2-md' : 'body-2')};

    &:focus-visible {
      outline: 2px solid ${theme.palette.primary[500]};
      outline-offset: -2px;
      border-radius: 10px;
    }
  `,
);

export const SItemIcon = styled(
  'span',
  styledOmitCustomProps,
)<Pick<SItemProps, '$variant'>>(
  ({ theme, $variant }) => css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    ${squareBlockCSS($variant === 'primary' ? 20 : 19)};
    color: ${$variant === 'primary' ? theme.palette.grey[700] : theme.palette.grey[400]};

    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  `,
);

export const SItemTitle = styled('span')(
  () => css`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
);

export const SItemAction = styled(ButtonBase)(
  ({ theme }) => css`
    ${squareBlockCSS(24)};
    flex-shrink: 0;
    margin-right: 12px;
    border-radius: 7px;
    color: ${theme.palette.grey[500]};
    opacity: 0;
    transition:
      opacity 0.12s ease,
      background-color 0.12s ease;

    &:hover {
      background-color: ${theme.palette.grey[200]};
      color: ${theme.palette.grey[700]};
    }

    &:focus-visible {
      opacity: 1;
      outline: 2px solid ${theme.palette.primary[500]};
      outline-offset: -2px;
    }
  `,
);

export const SHintTooltip = styled(Tooltip)(
  ({ theme }) => css`
    &&& .${tooltipClasses.tooltip} {
      position: relative;
      display: flex;
      align-items: center;
      height: 36px;
      max-width: none;
      margin: 0;
      padding: 0 9px;
      border-radius: 8px;
      background: ${theme.palette.grey[900]};
      box-shadow: 0 8px 20px -6px ${alpha(theme.palette.grey[900], 0.5)};
      color: ${theme.palette.common.white};
      font-family: ${theme.typography.fontFamily};
      font-size: 14px;
      font-weight: 500;
      line-height: 1;
      white-space: nowrap;

      &::after {
        position: absolute;
        right: 22px;
        bottom: 100%;
        border: 5px solid transparent;
        border-bottom-color: ${theme.palette.grey[900]};
        content: '';
      }
    }
  `,
);
